/**
 * ================================================================================================
 *  הסדרת תשלום — WHAT THE OFFICE OWES, AND THE ONE ACT IT MAY PERFORM ABOUT IT
 * ================================================================================================
 *
 * The Owner's §10:
 *
 *     *"כשל בחיוב יטופל בשלושה שלבים: התראה תוך המשך עבודה רגילה; לאחר 14 יום, הסדרה בלבד;
 *      לאחר 30 יום, הרשמה מחדש בלי שחזור אוטומטי ובלי ניסיון חדש אוטומטי."*
 *
 * ── THE PRESS THAT CHARGED A CARD IS GONE. **2026-11-03.** ────────────────────────────────────
 *
 * The Owner, settling the question this panel was built on:
 *
 *     *"הלקוח מנהל את המנוי שלו, ואני מנהל את החיוב... הלקוח אינו יכול להפעיל חיוב בכרטיס בעצמו.
 *      אם tenant.billing.settle מבצע סליקה בלחיצה על „שלם עכשיו", התאם את המסלול להנחיה הזאת.
 *      במסך ההסדרה הלקוח יוכל לראות את החוב ולעדכן כרטיס, והחיוב יחכה לאישור שלי בפלטפורמה.
 *      אכוף את ההפרדה גם בשרת."*
 *
 * Until today *"תשלום עכשיו"* ran `BillingRunService.run` with `apply: true`, filtered to this
 * office's own subscription — a real charge, initiated by the customer, with no platform approval
 * anywhere in the path. The reasoning behind it was not stupid: an office told *"הסדרה בלבד"*
 * could otherwise do nothing but wait for 03:20. But the authority over a charge is the
 * platform's, and *"הסדרה בלבד"* cannot be allowed to mean *"take money by self-service"*.
 *
 * THE ROUTE IS GONE TOO, not merely the button. A customer-facing act that is forbidden must be
 * unreachable on the server, or the rule is a decoration on a screen. `tenant.billing.settle` and
 * the capability `billing.settlement.pay` are removed; what the office keeps is the two READS and
 * the card.
 *
 * ── WHAT THE OFFICE CAN STILL DO, AND IT IS THE THING THAT ACTUALLY HELPS ─────────────────────
 *
 * UPDATE THE CARD. A charge fails because the instrument failed — expired, blocked, a new number.
 * Replacing it is the act that changes the outcome, and it is the office's to perform. The charge
 * that follows is made on the platform, by a person, after approval — which this panel says in
 * words rather than leaving the office to wonder why nothing happened after they fixed the card.
 *
 * ── AND IT STILL SAYS THAT A NEW CARD IS NOT A PAYMENT ────────────────────────────────────────
 *
 * 0038's header: *"עדכון אמצעי תשלום אינו הסדרה."* That was written when the panel offered both
 * acts and had to keep them apart. It matters MORE now, not less: the office performs the only
 * act available to it, and must not leave believing the debt is closed.
 *
 * ── IT IS SHOWN ONLY WHEN THERE IS SOMETHING TO SETTLE ────────────────────────────────────────
 *
 * `stage === 'current'` with a policy in force hides it. A permanent panel about a debt that does
 * not exist is a panel people learn to scroll past, and this one has to be read on the day it
 * appears. `governed === false` is NOT a calm state: with no dunning policy the stage cannot be
 * computed, so an office with a failed attempt is shown the panel anyway — what it owes is a fact
 * whatever the estate has configured.
 */

import { StatusChip } from './StatusChip';
import type { BillingStatement } from '../api/contract';
import { paymentStageHe } from '../api/billingText';
import { useLocale } from '../i18n/LocaleProvider';
import { routes } from '../routes';

export function SettlementPanel({
  statement,
}: {
  readonly statement: BillingStatement;
}): JSX.Element | null {
  const { t } = useLocale();

  const standing = statement.payment;
  /*
   * NOTHING TO SETTLE IS NOTHING TO SHOW. `current` WITH a policy in force means no open failure
   * cycle — the ordinary state of a paying office. Without a policy the stage cannot be computed,
   * so `attempts` is what says whether a charge has actually failed.
   */
  const unsettled = standing.stage !== 'current' || standing.attempts > 0;
  if (!unsettled) return null;

  const stage = paymentStageHe(standing.stage, standing.governed);

  return (
    <section className="notice" data-tone="warning" data-area="settlement">
      <h2 className="notice__title">{t('settle.title')}</h2>

      <p className="notice__body">
        <StatusChip labelHe={stage.labelHe} tone={stage.tone} />
      </p>
      <p className="notice__body">
        {standing.unsettledSince === null
          ? t('settle.noAnchor')
          : t('settle.since').replace('{date}', standing.unsettledSince)}
      </p>

      {/*
        WHO CHARGES, SAID BEFORE THE ONE BUTTON. An office that fixes its card and then sees
        nothing happen will either press again somewhere or call — and both are the product failing
        to say what it already knows: the charge is made on the platform, after a person approves
        it. This is that sentence.
      */}
      <p className="notice__body">{t('settle.platformCharges')}</p>

      {/*
        AND THE DISTINCTION 0038 NAMED, STILL HERE. *"עדכון אמצעי תשלום אינו הסדרה."* The office
        is about to perform the only act it has; it must not leave believing the debt is closed.
      */}
      <p className="notice__body">{t('settle.cardIsNotSettlement')}</p>

      <div className="actions-row">
        {/*
          A LINK, AND DRESSED AS ONE. `btn--primary` is the treatment a WRITE wears, and
          `actionAffordance.test.ts` refuses an `<a>` wearing it: navigation that looks like an
          action teaches a person that the primary button sometimes only moves them. This used to
          sit beside a press that charged a card; now that the press is gone it is the only control
          here, and the temptation to promote it is exactly what that control exists to refuse.
        */}
        <a className="btn" href={routes.tenantBilling}>
          {t('settle.updateCard')}
        </a>
      </div>
    </section>
  );
}
